import { useQuery } from "@tanstack/react-query";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip as RechartsTooltip, XAxis, YAxis } from "recharts";
import { Activity, Clock } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useBrandColors } from "@/lib/brand-colors";
import type { DetailedUserActivity, EngagementBucket } from "@shared/schema";

const BUCKET_CLASS: Record<EngagementBucket, string> = {
  "Active": "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200",
  "Cooling off": "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200",
  "At risk": "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200",
  "Dormant": "bg-muted text-muted-foreground",
};

function formatDayLabel(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export function UserActivityDrilldownDialog({
  userId,
  userLabel,
  onClose,
}: {
  userId: number | null;
  userLabel: string;
  onClose: () => void;
}) {
  const open = userId != null;
  const brandColors = useBrandColors();

  const activityQuery = useQuery<DetailedUserActivity>({
    queryKey: ["/api/admin/app-users", userId, "activity"],
    enabled: open,
  });

  const chartData = (activityQuery.data?.days ?? []).map((d) => ({
    date: d.date,
    label: formatDayLabel(d.date),
    hits: d.hits,
  }));
  const stats = activityQuery.data?.stats;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="dialog-user-activity-drilldown"
      >
        <DialogHeader>
          <DialogTitle data-testid="text-activity-drilldown-title">Activity — {userLabel}</DialogTitle>
          <p className="text-xs text-muted-foreground">Daily page hits over the last 30 days</p>
        </DialogHeader>

        {activityQuery.isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-64 w-full" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-16 w-full" />)}
            </div>
          </div>
        ) : activityQuery.isError ? (
          <div className="text-sm text-destructive" data-testid="text-activity-drilldown-error">
            Failed to load this user's activity.
          </div>
        ) : activityQuery.data ? (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              {stats && (
                <Badge
                  className={`inline-flex items-center gap-1 ${BUCKET_CLASS[stats.engagementBucket] ?? "bg-muted text-muted-foreground"}`}
                  data-testid="badge-activity-drilldown-bucket"
                >
                  <Activity className="h-3 w-3" />
                  {stats.engagementBucket}
                </Badge>
              )}
              {stats?.lastActive ? (
                <span className="inline-flex items-center gap-1 text-xs text-muted-foreground" data-testid="text-activity-drilldown-last-active">
                  <Clock className="h-3 w-3" />
                  Last active {stats.lastActive}
                  {stats.daysSinceActive !== null && (
                    <> ({stats.daysSinceActive === 0 ? "today" : `${stats.daysSinceActive}d ago`})</>
                  )}
                </span>
              ) : (
                <span className="text-xs text-muted-foreground" data-testid="text-activity-drilldown-last-active">
                  No recorded activity
                </span>
              )}
            </div>

            <Card data-testid="card-activity-drilldown-chart">
              <div className="p-3">
                <div className="h-64 w-full" data-testid="chart-activity-drilldown">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-border" vertical={false} />
                      <XAxis dataKey="label" fontSize={11} minTickGap={16} tickLine={false} />
                      <YAxis allowDecimals={false} fontSize={11} width={40} />
                      <RechartsTooltip
                        formatter={(value) => [String(value), "Page hits"]}
                        contentStyle={{ fontSize: 12 }}
                        cursor={{ fill: "hsl(var(--muted))", opacity: 0.4 }}
                      />
                      <Bar dataKey="hits" name="Page hits" fill={brandColors.brand} radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </Card>

            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card className="p-3">
                  <div className="text-xs text-muted-foreground">Days active (7d)</div>
                  <div className="text-lg font-semibold" data-testid="text-activity-drilldown-days7">{stats.activeDays7}</div>
                </Card>
                <Card className="p-3">
                  <div className="text-xs text-muted-foreground">Days active (30d)</div>
                  <div className="text-lg font-semibold" data-testid="text-activity-drilldown-days30">{stats.activeDays30}</div>
                </Card>
                <Card className="p-3">
                  <div className="text-xs text-muted-foreground">Avg hits/day (7d)</div>
                  <div className="text-lg font-semibold" data-testid="text-activity-drilldown-avg7">{stats.avgPageHitsPerDay7}</div>
                </Card>
                <Card className="p-3">
                  <div className="text-xs text-muted-foreground">Avg hits/day (30d)</div>
                  <div className="text-lg font-semibold" data-testid="text-activity-drilldown-avg30">{stats.avgPageHitsPerDay30}</div>
                </Card>
              </div>
            )}
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="button-close-activity-drilldown">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
